import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { JobCategoryService, JobCategory, CategoryCV, CVAnalysis } from '../../../services/job-category.service';
import { CVScoringService, ScoringResult, CVScore } from '../../../services/cv-scoring.service';
import { Observable, forkJoin } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-cv-pool',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './cv-pool.component.html',
  styleUrls: ['./cv-pool.component.scss']
})
export class CVPoolComponent implements OnInit, OnDestroy {
  // Data
  jobCategories$!: Observable<JobCategory[]>;
  selectedCategory: JobCategory | null = null;
  categoryCVs: CategoryCV[] = [];
  cvAnalysis: CVAnalysis | null = null;
  scoringResults: ScoringResult | null = null;
  
  // UI State
  isLoadingCategories = false;
  isLoadingCVs = false;
  isLoadingAnalysis = false;
  isLoadingMatches = false;
  isLoadingScoring = false;
  
  // Forms
  categoryForm: FormGroup;
  editCategoryForm: FormGroup;
  matchingForm: FormGroup;
  
  // File upload
  selectedFile: File | null = null;
  selectedFiles: File[] = [];
  isUploading = false;
  uploadProgress: { [key: string]: number } = {};
  uploadResults: any[] = [];
  
  // Modals
  showCategoryModal = false;
  showEditCategoryModal = false;
  showCVAnalysisModal = false;
  showMatchingModal = false;
  showScoringModal = false;
  showUploadModal = false;
  showSuccessModal = false;
  showConfirmModal = false;
  
  // Toast notification
  showToast = false;
  toastMessage = '';
  toastType = 'success'; // success, error, info
  
  // Filters
  cvStatusFilter = '';
  searchTerm = '';
  
  // Popup data
  successMessage = '';
  confirmMessage = '';
  confirmCallback: (() => void) | null = null;
  cvToDelete: CategoryCV | null = null;
  
  // Auto-refresh
  private refreshInterval: any = null;
  private isRefreshing = false;
  private activeScoringRequestId: number | null = null;
  
  // Recommendation modal
  showRecommendationModal = false;
  fullRecommendation = '';
  candidateName = '';
  
  // Edit category
  editingCategory: any = null;
  isUpdatingCategory = false;

  constructor(
    private jobCategoryService: JobCategoryService,
    private cvScoringService: CVScoringService,
    private fb: FormBuilder
  ) {
    this.categoryForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      requirements: ['']
    });

    this.editCategoryForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      requirements: ['']
    });

    this.matchingForm = this.fb.group({
      description: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.loadJobCategories();
    this.startAutoRefresh();
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  // Auto-refresh functions
  startAutoRefresh(): void {
    // Check every 3 seconds for processing CVs
    this.refreshInterval = setInterval(() => {
      this.checkProcessingCVs();
    }, 3000);
  }

  stopAutoRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  checkProcessingCVs(): void {
    if (this.isRefreshing || !this.selectedCategory) return;
    
    // Check if there are any processing CVs
    const hasProcessingCVs = this.categoryCVs.some(cv => cv.analysisStatus === 'processing');
    
    if (hasProcessingCVs) {
      this.isRefreshing = true;
      this.loadCategoryCVs(this.selectedCategory.id);
    }
  }

  loadJobCategories(): void {
    this.isLoadingCategories = true;
    this.jobCategories$ = this.jobCategoryService.getJobCategories().pipe(
      map(response => {
        this.isLoadingCategories = false;
        return response.data;
      })
    );
  }

  selectCategory(category: JobCategory): void {
    this.selectedCategory = category;
    this.setScoringResults(null); // Clear scoring results when switching categories
    this.loadCategoryCVs(category.id);
    this.loadScoringResults(category.id);
  }

  loadCategoryCVs(categoryId: number): void {
    this.isLoadingCVs = true;
    this.jobCategoryService.getCategoryCVs(categoryId, { limit: 100 }).subscribe({
      next: (response) => {
        this.categoryCVs = response.data;
        this.isLoadingCVs = false;
        this.isRefreshing = false; // Reset refresh flag
        
        // Check if any CVs completed processing
        const completedCVs = this.categoryCVs.filter(cv => cv.analysisStatus === 'completed');
        if (completedCVs.length > 0) {
          console.log(`${completedCVs.length} CV(s) completed processing`);
        }
      },
      error: (error) => {
        console.error('Error loading CVs:', error);
        this.isLoadingCVs = false;
        this.isRefreshing = false;
      }
    });
  }

  loadScoringResults(categoryId: number): void {
    this.activeScoringRequestId = categoryId;
    this.isLoadingScoring = true;

    this.cvScoringService.getScoringResults(categoryId).subscribe({
      next: (response) => {
        if (this.activeScoringRequestId !== categoryId) {
          return;
        }

        this.isLoadingScoring = false;
        this.activeScoringRequestId = null;

        if (response.success && response.data) {
          this.setScoringResults(response.data);
        } else {
          this.setScoringResults(null);
        }
      },
      error: (error) => {
        if (this.activeScoringRequestId !== categoryId) {
          return;
        }

        this.isLoadingScoring = false;
        this.activeScoringRequestId = null;

        if (error.status === 404) {
          this.setScoringResults(null);
        } else {
          console.error('Error loading scoring results:', error);
        }
      }
    });
  }

  openCategoryModal(): void {
    this.categoryForm.reset();
    this.showCategoryModal = true;
  }

  editCategory(category: any, event: Event): void {
    event.stopPropagation(); // Prevent card click
    this.editingCategory = category;
    this.editCategoryForm.patchValue({
      title: category.title,
      description: category.description || '',
      requirements: category.requirements || ''
    });
    this.showEditCategoryModal = true;
  }

  updateCategory(): void {
    if (this.editCategoryForm.valid && this.editingCategory) {
      this.isUpdatingCategory = true;
      const categoryData = this.editCategoryForm.value;
      
      this.jobCategoryService.updateJobCategory(this.editingCategory.id, categoryData).subscribe({
        next: (response) => {
          console.log('Category updated successfully:', response);
          this.showEditCategoryModal = false;
          this.editingCategory = null;
          this.isUpdatingCategory = false;
          this.loadJobCategories();
          this.showToastNotification('Category updated successfully!', 'success');
        },
        error: (error) => {
          console.error('Error updating category:', error);
          this.isUpdatingCategory = false;
          this.showToastNotification('Error updating category', 'error');
        }
      });
    }
  }

  deleteCategory(category: JobCategory, event: Event): void {
    event.stopPropagation(); // Prevent card click
    this.showConfirmPopup(
      `"${category.title}" kategorisini silmek istediğinize emin misiniz?`,
      () => {
        this.jobCategoryService.deleteJobCategory(category.id).subscribe({
          next: () => {
            if (this.selectedCategory?.id === category.id) {
              this.selectedCategory = null;
              this.categoryCVs = [];
              this.setScoringResults(null);
            }
            this.loadJobCategories();
            this.showToastNotification('Kategori başarıyla silindi!', 'success');
          },
          error: (error) => {
            console.error('Error deleting category:', error);
            const message = error?.error?.message || 'Kategori silinirken bir hata oluştu.';
            this.showToastNotification(message, 'error');
          }
        });
      }
    );
  }

  createCategory(): void {
    if (this.categoryForm.valid) {
      const categoryData = this.categoryForm.value;
      
      // Call the job position creation API
      this.jobCategoryService.createJobCategory(categoryData).subscribe({
        next: (response) => {
          console.log('Category created successfully:', response);
          this.showCategoryModal = false;
          this.loadJobCategories();
          },
          error: (error) => {
          console.error('Error creating category:', error);
        }
      });
    }
  }

  openUploadModal(): void {
    this.selectedFile = null;
    this.selectedFiles = [];
    this.showUploadModal = true;
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFiles = Array.from(input.files);
      console.log(`Selected ${this.selectedFiles.length} files:`, this.selectedFiles.map(f => f.name));
    }
  }

  uploadFiles(): void {
    if (this.selectedFiles.length > 0 && this.selectedCategory) {
      this.isUploading = true;
      this.uploadResults = [];
      
      // If only one file, use single upload for better performance
      if (this.selectedFiles.length === 1) {
        this.jobCategoryService.uploadCVToCategory(this.selectedCategory.id, this.selectedFiles[0]).subscribe({
          next: (response) => {
            console.log('CV uploaded successfully:', response);
            this.isUploading = false;
            this.showUploadModal = false;
            this.loadCategoryCVs(this.selectedCategory!.id);
            this.showSuccessPopup('CV uploaded successfully!');
      },
      error: (error) => {
            console.error('Error uploading CV:', error);
            this.isUploading = false;
            
            // Handle duplicate CV error
            if (error.error && error.error.duplicate) {
              const existingCV = error.error.existingCV;
              const message = `CV "${existingCV.fileName}" already exists!\n` +
                            `Applicant: ${existingCV.applicantName}\n` +
                            `Uploaded: ${new Date(existingCV.uploadedAt).toLocaleDateString()}\n` +
                            `Status: ${existingCV.analysisStatus}\n\n` +
                            `Please delete the existing CV first or use a different filename.`;
              
              this.showToastNotification(message, 'error');
        } else {
              this.showToastNotification('Error uploading CV. Please try again.', 'error');
            }
          }
        });
      } else {
        // Multiple files, use multiple upload
        this.jobCategoryService.uploadMultipleCVsToCategory(this.selectedCategory.id, this.selectedFiles).subscribe({
          next: (response) => {
            console.log('Multiple CVs uploaded successfully:', response);
            this.uploadResults = response.data.uploaded || [];
            this.isUploading = false;
            this.showUploadModal = false;
            this.loadCategoryCVs(this.selectedCategory!.id);
            
            // Show success message with detailed breakdown
            const successCount = response.data.successfulUploads || 0;
            const failCount = response.data.failedUploads || 0;
            const duplicateCount = response.data.errors?.filter((err: any) => err.duplicate).length || 0;
            
            let message = `${successCount} CV(s) uploaded successfully`;
            if (duplicateCount > 0) {
              message += `, ${duplicateCount} duplicate(s) skipped`;
            }
            if (failCount > 0) {
              message += `, ${failCount} failed`;
            }
            
            this.showToastNotification(message, 'success');
          },
          error: (error) => {
            console.error('Error uploading multiple CVs:', error);
            this.isUploading = false;
            
            // Handle multiple upload errors
            if (error.error && error.error.errors && error.error.errors.length > 0) {
              const duplicateErrors = error.error.errors.filter((err: any) => err.duplicate);
              const otherErrors = error.error.errors.filter((err: any) => !err.duplicate);
              
              let errorMessage = '';
              if (duplicateErrors.length > 0) {
                errorMessage += `${duplicateErrors.length} duplicate CV(s) skipped:\n`;
                duplicateErrors.forEach((err: any, index: number) => {
                  if (index < 3) { // Show first 3 duplicates
                    errorMessage += `• ${err.fileName} (${err.existingCV.applicantName})\n`;
                  }
                });
                if (duplicateErrors.length > 3) {
                  errorMessage += `• ... and ${duplicateErrors.length - 3} more\n`;
                }
              }
              if (otherErrors.length > 0) {
                errorMessage += `${otherErrors.length} CV(s) failed to upload.`;
              }
              
              this.showToastNotification(errorMessage, 'error');
            } else {
              this.showToastNotification('Error uploading CVs. Please try again.', 'error');
            }
          }
        });
      }
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  viewCVAnalysis(cv: CategoryCV): void {
    this.isLoadingAnalysis = true;
    this.jobCategoryService.getCVAnalysis(cv.id).subscribe({
          next: (response) => {
        this.cvAnalysis = response.data;
        this.isLoadingAnalysis = false;
        this.showCVAnalysisModal = true;
      },
      error: (error) => {
        console.error('Error loading CV analysis:', error);
        this.isLoadingAnalysis = false;
      }
    });
  }

  reanalyzeCV(cv: CategoryCV): void {
    this.jobCategoryService.reanalyzeCV(cv.id).subscribe({
      next: (response) => {
        console.log('CV re-analysis started:', response);
        this.loadCategoryCVs(this.selectedCategory!.id);
      },
      error: (error) => {
        console.error('Error re-analyzing CV:', error);
      }
    });
  }

  deleteCV(cv: CategoryCV): void {
    this.cvToDelete = cv;
    this.showConfirmPopup(
      `Are you sure you want to delete ${cv.applicantName}'s CV?`,
      () => {
        this.jobCategoryService.deleteCV(cv.id).subscribe({
          next: (response) => {
            console.log('CV deleted successfully:', response);
            this.loadCategoryCVs(this.selectedCategory!.id);
            this.showToastNotification('CV deleted successfully!', 'success');
          },
          error: (error) => {
            console.error('Error deleting CV:', error);
            this.showToastNotification('Error deleting CV. Please try again.', 'error');
          }
        });
      }
    );
  }

  openMatchingModal(): void {
    this.matchingForm.patchValue({
      description: this.selectedCategory?.description || ''
    });
    this.showMatchingModal = true;
  }

  performMatching(): void {
    if (this.matchingForm.valid && this.selectedCategory) {
      this.isLoadingMatches = true;
      const description = this.matchingForm.get('description')?.value;
      
      this.jobCategoryService.matchCategoryCVs(this.selectedCategory.id, description).subscribe({
          next: (response) => {
          console.log('Matching completed:', response);
          this.isLoadingMatches = false;
          this.showMatchingModal = false;
              },
              error: (error) => {
          console.error('Error performing matching:', error);
          this.isLoadingMatches = false;
        }
      });
    }
  }


  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'completed': return 'badge bg-success';
      case 'processing': return 'badge bg-warning';
      case 'pending': return 'badge bg-secondary';
      case 'failed': return 'badge bg-danger';
      default: return 'badge bg-secondary';
    }
  }


  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  get filteredCVs(): CategoryCV[] {
    let filtered = this.categoryCVs;
    
    if (this.cvStatusFilter) {
      filtered = filtered.filter(cv => cv.analysisStatus === this.cvStatusFilter);
    }
    
    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter(cv => 
        cv.applicantName.toLowerCase().includes(term) ||
        cv.applicantEmail?.toLowerCase().includes(term) ||
        cv.fileName.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  }

  openCVFile(cv: CategoryCV): void {
    if (!cv || !cv.id || !cv.hasFile) {
      return;
    }

    this.jobCategoryService.getCVFile(cv.id).subscribe({
      next: (blob) => {
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL, '_blank');
        setTimeout(() => URL.revokeObjectURL(fileURL), 30000);
      },
      error: (error) => {
        console.error('Error opening CV file:', error);
        const message = error?.error?.message || 'Dosya açılırken bir hata oluştu.';
        this.showToastNotification(message, 'error');
      }
    });
  }

  closeModals(): void {
    this.showCategoryModal = false;
    this.showEditCategoryModal = false;
    this.showCVAnalysisModal = false;
    this.showMatchingModal = false;
    this.showScoringModal = false;
    this.showUploadModal = false;
    this.showSuccessModal = false;
    this.showConfirmModal = false;
    this.showRecommendationModal = false;
  }

  // Popup Functions
  showSuccessPopup(message: string): void {
    this.successMessage = message;
    this.showSuccessModal = true;
  }

  showConfirmPopup(message: string, action: () => void): void {
    this.confirmMessage = message;
    this.confirmCallback = action;
    this.showConfirmModal = true;
  }

  // Toast notification methods
  showToastNotification(message: string, type: 'success' | 'error' | 'info' = 'success'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.showToast = true;
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      this.showToast = false;
    }, 3000);
  }

  hideToast(): void {
    this.showToast = false;
  }

  formatToastMessage(message: string): string {
    return message.replace(/\n/g, '<br>');
  }

  confirmAction(): void {
    if (this.confirmCallback) {
      this.confirmCallback();
      this.showConfirmModal = false;
      this.confirmCallback = null;
    }
  }

  cancelAction(): void {
    this.showConfirmModal = false;
    this.confirmCallback = null;
  }

  // Recommendation functions
  getShortRecommendation(recommendation: any): string {
    if (!recommendation) return 'No recommendation';
    
    // Handle both string and object cases
    let recommendationText = '';
    if (typeof recommendation === 'string') {
      recommendationText = recommendation;
    } else if (typeof recommendation === 'object') {
      // Handle new AI format with structured recommendation
      if (recommendation.jobPositionSuitability && recommendation.interviewPriority) {
        recommendationText = `${recommendation.jobPositionSuitability} UYGUNLUK - ${recommendation.interviewPriority} görüşme önceliği`;
      } else if (recommendation.text) {
        recommendationText = recommendation.text;
      } else if (recommendation.recommendation) {
        recommendationText = recommendation.recommendation;
      } else {
        recommendationText = JSON.stringify(recommendation);
      }
    } else {
      recommendationText = JSON.stringify(recommendation);
    }
    
    if (recommendationText.length <= 50) return recommendationText;
    return recommendationText.substring(0, 47) + '...';
  }

  showFullRecommendation(recommendation: any, candidateName: string): void {
    // Handle both string and object cases
    let recommendationText = '';
    if (typeof recommendation === 'string') {
      recommendationText = recommendation;
    } else if (typeof recommendation === 'object') {
      // Handle new AI format with structured recommendation
      if (recommendation.jobPositionSuitability && recommendation.interviewPriority) {
        recommendationText = this.formatStructuredRecommendation(recommendation);
      } else if (recommendation.text) {
        recommendationText = recommendation.text;
      } else if (recommendation.recommendation) {
        recommendationText = recommendation.recommendation;
      } else {
        recommendationText = JSON.stringify(recommendation);
      }
    } else {
      recommendationText = JSON.stringify(recommendation);
    }
    
    this.fullRecommendation = recommendationText;
    this.candidateName = candidateName;
    this.showRecommendationModal = true;
  }

  formatStructuredRecommendation(rec: any): string {
    let formatted = '';
    
    // Header
    formatted += `🎯 ${rec.jobPositionSuitability} UYGUNLUK - ${rec.interviewPriority} görüşme önceliği\n\n`;
    
    // Technical topics
    if (rec.technicalTopicsToAsk && rec.technicalTopicsToAsk.length > 0) {
      formatted += `📋 Hangi Teknik Konular Sorulmalı:\n`;
      rec.technicalTopicsToAsk.forEach((topic: string, index: number) => {
        formatted += `${index + 1}. ${topic}\n`;
      });
      formatted += '\n';
    }
    
    // Projects to test
    if (rec.projectsToTest && rec.projectsToTest.length > 0) {
      formatted += `🧪 Hangi Projeler Test Edilmeli:\n`;
      rec.projectsToTest.forEach((project: string, index: number) => {
        formatted += `${index + 1}. ${project}\n`;
      });
      formatted += '\n';
    }
    
    // Team contribution
    if (rec.teamContributionPotential) {
      formatted += `💼 Takıma Katkı Potansiyeli:\n${rec.teamContributionPotential}\n\n`;
    }
    
    // Development needs
    if (rec.developmentNeeds && rec.developmentNeeds.length > 0) {
      formatted += `📈 Gelişim İhtiyaçları:\n`;
      rec.developmentNeeds.forEach((need: string, index: number) => {
        formatted += `${index + 1}. ${need}\n`;
      });
    }
    
    return formatted;
  }

  closeRecommendationModal(): void {
    this.showRecommendationModal = false;
    this.fullRecommendation = '';
    this.candidateName = '';
  }

  // CV Scoring Functions
  private setScoringResults(result: ScoringResult | null): void {
    if (result && Array.isArray(result.scores)) {
      const sortedScores = [...result.scores].sort((a, b) => (b.totalScore || 0) - (a.totalScore || 0));
      this.scoringResults = {
        ...result,
        scores: sortedScores
      };
    } else if (result) {
      this.scoringResults = result;
    } else {
      this.scoringResults = null;
    }
  }

  openScoringModal(): void {
    if (this.selectedCategory) {
      // Don't reset scoringResults - keep them persistent
      this.isLoadingScoring = false;
      this.showScoringModal = true;
    }
  }

  performScoring(): void {
    const startTime = Date.now();
    console.log(`🎯 Frontend Scoring Started at ${new Date().toISOString()}`);
    console.log(`📊 Selected Category: ${this.selectedCategory?.title}`);
    console.log(`📝 Description Length: ${this.selectedCategory?.description?.length || 0} characters`);
    console.log(`👥 Available CVs: ${this.categoryCVs?.length || 0}`);
    
    if (!this.selectedCategory || !this.selectedCategory.description) {
      console.log('❌ Missing job description or category');
      this.showSuccessPopup('Job description is required for scoring');
      return;
    }

    console.log(`⏰ Starting scoring request...`);
    this.isLoadingScoring = true;
    const categoryId = this.selectedCategory.id;
    this.activeScoringRequestId = categoryId;
    
    this.cvScoringService.scoreJobPositionCVs(categoryId, this.selectedCategory.description).subscribe({
      next: (response) => {
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        if (this.activeScoringRequestId !== categoryId) {
          return;
        }

        console.log(`✅ Frontend scoring completed in ${duration}ms`);
        console.log(`📊 Response received:`, response);
        console.log(`📊 Response success: ${response.success}`);
        console.log(`📊 Response data:`, response.data);
        console.log(`📊 Response data type:`, typeof response.data);
        console.log(`📊 Response data keys:`, response.data ? Object.keys(response.data) : 'No data');
        
        this.isLoadingScoring = false;
        this.activeScoringRequestId = null;
        
        if (!response.success || !response.data) {
          console.error('❌ Frontend: Scoring response indicates failure');
          this.showSuccessPopup('Error performing CV scoring. Please try again.');
          return;
        }

        // Set scoring results with proper structure
        this.setScoringResults(response.data);
        console.log('🔧 Frontend: scoringResults set successfully');
        console.log('🔧 Frontend: scoringResults.scores length:', this.scoringResults?.scores?.length || 0);
        console.log('🔧 Frontend: All scores:', this.scoringResults?.scores?.map(s => s.applicantName));
        
        // Refresh CV list to reflect persisted scoring data
        this.loadCategoryCVs(categoryId);
        
        // Modal'ı kapatma, sonuçları göster
        console.log('🎯 Scoring completed:', response);
        console.log('📊 Scores before display:', this.scoringResults?.scores);
        console.log('🏆 Top 5 scores:', this.scoringResults?.scores?.slice(0, 5));
        console.log('📈 Score consistency check:');
        this.scoringResults?.scores?.forEach((score, index) => {
          console.log(`${index + 1}. ${score.applicantName}: ${score.totalScore}/100`);
        });
      },
      error: (error) => {
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        console.error('❌ Frontend scoring error:', error);
        console.error('📊 Error type:', error.name);
        console.error('📊 Error message:', error.message);
        console.error('📊 Error status:', error.status);
        console.error('📊 Error statusText:', error.statusText);
        console.error('📊 Total time before error:', duration + 'ms');
        console.error('📊 Error details:', error.error);
        
        if (this.activeScoringRequestId === categoryId) {
          this.isLoadingScoring = false;
          this.activeScoringRequestId = null;
        }
        this.showSuccessPopup('Error performing CV scoring. Please try again.');
      }
    });
  }

  // Re-score function - same as performScoring but with different messaging
  reScoreCVs(): void {
    this.performScoring();
  }

  getScoreColor(score: number): string {
    if (score >= 90) return 'text-success';
    if (score >= 80) return 'text-warning';
    if (score >= 70) return 'text-info';
    return 'text-danger';
  }

  getScoreBadgeClass(score: number): string {
    if (score >= 90) return 'badge bg-success';
    if (score >= 80) return 'badge bg-warning';
    if (score >= 70) return 'badge bg-info';
    return 'badge bg-danger';
  }
}
